import { faArrowLeft, faCheck, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Input } from "@ventumsoftware/react-toolkit";
import { type FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import colors from "tailwindcss/colors";
import { useApi } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { Company } from "../../api/dto";
import { CompanyLogo } from "../../components/companies/CompanyLogo";
import { resolveCompanyColorPalette } from "../../components/companies/companyColorPalette";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { useModal } from "../../components/layout/ModalProvider";
import { AddCompanyLogoModal } from "../../modals/AddCompanyLogoModal";

export const CompanySettingsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const { companies } = useApi();

  const company = useMemo(() => {
    return companies.find((c) => c.id === companyId);
  }, [companies, companyId]);

  return (
    <div className={""}>
      <ContentContainer size={"sm"}>
        <Button
          type={"blank"}
          icon={faArrowLeft}
          size={"md"}
          text={"Terug naar bedrijf"}
          onClick={() => navigate(`/companies/${companyId}`)}
        />
        <PageHeader>Instellingen: {company?.name}</PageHeader>

        {company && (
          <>
            <CompanyDetailsForm company={company} />
            <CompanyThemeForm company={company} />
            <CompanyQuestionSetsForm company={company} />
          </>
        )}
      </ContentContainer>
    </div>
  );
};

const CompanyDetailsForm: FC<{ company: Company }> = ({ company }) => {
  const [name, setName] = useState(company.name);
  const [alias, setAlias] = useState(company.alias ?? "");

  const { refresh } = useApi();
  const { updateCompany } = useApiCall();

  const isDirty = name !== company.name || alias !== (company.alias ?? "");

  const saveCompany = async () => {
    await updateCompany(company.id, name, undefined, alias);
    await refresh.companies(true);
  };
  return (
    <Card title={"Bedrijf"}>
      <div className={"flex flex-col items-stretch space-y-4"}>
        <Input type={"text"} label={"Naam"} value={name} onChange={setName} />
        <Input type={"text"} label={"Alias"} value={alias} onChange={setAlias} />
      </div>
      <hr className={"-mx-6 border-slate-200 mt-6 mb-4"} />
      <Button type={"primary"} size={"md"} text={"Opslaan"} disabled={!isDirty} onClick={() => saveCompany()} />
    </Card>
  );
};

const defaultColors = Object.keys(colors).filter(
  (k) =>
    ![
      "inherit",
      "current",
      "transparent",
      "black",
      "white",
      "lightBlue",
      "warmGray",
      "trueGray",
      "coolGray",
      "blueGray",
      "neutral",
    ].includes(k),
);
const CompanyThemeForm: FC<{ company: Company }> = ({ company }) => {
  const [colorPalette, setColorPalette] = useState(company.colorPalette);

  const { refresh } = useApi();
  const { updateCompany } = useApiCall();

  const isDirty = colorPalette !== company.colorPalette;

  const saveCompany = async () => {
    if (!colorPalette) {
      return;
    }
    await updateCompany(company.id, company.name, undefined, undefined, colorPalette);
    await refresh.companies(true);
  };
  const companyLogoModal = useModal({
    title: "Logo vervangen",
    body: (
      <AddCompanyLogoModal
        company={company}
        onUpdate={() => {
          refresh.companies(true);
        }}
      />
    ),
  });
  const resolvedPalette = colorPalette ? resolveCompanyColorPalette(colorPalette) : null;
  return (
    <Card title={"Merk & Huisstijl"}>
      <div className={"flex flex-col items-stretch space-y-4"}>
        <div className={"border border-slate-200 rounded-lg p-4 flex items-center justify-between"}>
          <CompanyLogo company={company} />
          <Button
            type={"secondary"}
            size={"md"}
            text={"Logo uploaden/vervangen"}
            icon={faUpload}
            onClick={() => companyLogoModal.open()}
          />
        </div>
        {/*<Input type={'text'} label={'Naam'} value={name} onChange={setName} />*/}
        {/*<Input type={'text'} label={'Alias'} value={alias} onChange={setAlias} />*/}
      </div>
      <hr className={"-mx-6 border-slate-200 mt-6 mb-4"} />
      <div>
        {/*{colorPalette && <style>{generateDynamicColorSchemeCSS(resolveCompanyColorPalette(colorPalette))}</style>}*/}

        <div className={"flex flex-wrap gap-2"}>
          {defaultColors.map((color, i) => {
            const resolvedPalette = resolveCompanyColorPalette(color);
            return (
              <label
                key={i}
                className={`flex items-center cursor-pointer border-2 rounded-lg py-1 px-2 ${colorPalette === color ? `border-slate-600 bg-slate-50 text-slate-950` : "border-slate-200 dark:border-zinc-500 text-black"}`}
              >
                <input
                  type="radio"
                  name={"palette"}
                  value={color}
                  key={color}
                  checked={colorPalette === color}
                  onChange={() => setColorPalette(color)}
                  className={"hidden"}
                />
                <div className={`h-4 w-4 rounded-sm mr-2`} style={{ background: resolvedPalette[500] }}></div>
                <span className={"text-sm font-medium"}>
                  {color.charAt(0).toUpperCase()}
                  {color.slice(1)}
                </span>
              </label>
            );
          })}
        </div>

        <div className={"mt-4 font-medium"}>Voorbeeld:</div>
        <div className={"grid grid-cols-12 gap-4 mt-3"}>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[50] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[100] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[200] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[300] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[400] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[500] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[600] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[700] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[800] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[900] }}></div>
          <div className={"aspect-square rounded-lg"} style={{ background: resolvedPalette?.[950] }}></div>
        </div>
      </div>
      <hr className={"-mx-6 border-slate-200 mt-6 mb-4"} />
      <Button
        type={"primary"}
        size={"md"}
        text={"Opslaan"}
        disabled={!isDirty || !colorPalette}
        onClick={() => saveCompany()}
      />
    </Card>
  );
};

const CompanyQuestionSetsForm: FC<{ company: Company }> = ({ company }) => {
  const { refresh, questionSets } = useApi();
  const { updateCompany } = useApiCall();

  const [selectedQuestionSets, setSelectedQuestionSets] = useState(company.availableQuestionSetIds);

  const isDirty = selectedQuestionSets.join(",") !== company.availableQuestionSetIds.join(",");

  const saveCompany = async () => {
    await updateCompany(company.id, company.name, undefined, undefined, undefined, selectedQuestionSets);
    await refresh.companies(true);
  };
  return (
    <Card title={"Vragenlijsten toegang"}>
      <div>
        {questionSets
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((qs, i) => {
            const enabled = selectedQuestionSets.includes(qs.id);
            return (
              <button
                key={i}
                className={`h-10 flex items-center hover:bg-brand-100 w-full rounded-sm font-medium px-2 ${enabled ? "text-brand-950" : "text-slate-700 opacity-75"}`}
                onClick={() => {
                  if (enabled) {
                    setSelectedQuestionSets((old) => old.filter((x) => x !== qs.id));
                  } else {
                    setSelectedQuestionSets((old) => [...old, qs.id]);
                  }
                }}
              >
                <span
                  className={`h-4.5 w-4.5 rounded-sm flex items-center justify-center mr-4 border-2 ${enabled ? "border-brand-700 bg-brand-700 text-white" : "border-slate-800"}`}
                >
                  {enabled ? <FontAwesomeIcon icon={faCheck} /> : <></>}
                </span>
                <span className={"text-sm"}>{qs.name}</span>
              </button>
            );
          })}
      </div>
      <hr className={"-mx-6 border-slate-200 mt-6 mb-4"} />
      <Button type={"primary"} size={"md"} text={"Opslaan"} disabled={!isDirty} onClick={() => saveCompany()} />
    </Card>
  );
};
